import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IAcceptAllDigitalDelivery,
  IAcceptDigitalDelivery,
  ICancelAllDigitalDelivery,
  ICancelDigitalDelivery,
  ICreateAllDigitalDeliveries,
  ICreateDigitalDelivery,
  IDigitalDeliveryHistoryItem,
  IDigitalDeliveryResponse,
  IUnsupportedRolesResponse,
  IUpdateAllDigitalDeliveries,
  IUpdateDigitalDelivery,
  IWithdrawAllDigitalDelivery,
  IWithdrawDigitalDelivery,
} from './types';

export const useGetDigitalDelivery = (
  MusicProductId: string,
  SalesChannelId: string,
  ProviderId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDigitalDeliveryResponse, DefaultError>({
    queryKey: ['digital-delivery', MusicProductId, SalesChannelId, ProviderId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `DigitalDelivery`,
        params: { MusicProductId, SalesChannelId, ProviderId },
      });

      return responseHandler(response, {
        groupName: `useGetDigitalDelivery - ${MusicProductId} - ${SalesChannelId} - ${ProviderId}`,
        notFoundPath: `/music-product-digital/${MusicProductId}/delivery`,
      });
    },
    enabled,
    retry: false,
  });
};

export const usePostDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostDigitalDelivery');
  return useMutation<{}, DefaultError, ICreateDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'DigitalDelivery',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostDigitalDelivery',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const usePostAllDigitalDeliveries = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostAllDigitalDeliveries'
  );
  return useMutation<{}, DefaultError, ICreateAllDigitalDeliveries>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'DigitalDelivery/All',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePostAllDigitalDeliveries',
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const usePutAllDigitalDeliveries = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutAllDigitalDeliveries'
  );
  return useMutation<{}, DefaultError, IUpdateAllDigitalDeliveries>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDelivery/All',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutAllDigitalDeliveries',
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutDigitalDelivery');
  return useMutation<{}, DefaultError, IUpdateDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDelivery',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutDigitalDelivery',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCancelDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useCancelDigitalDelivery'
  );
  return useMutation<{}, DefaultError, ICancelDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.cancel.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDelivery/Cancel',
        body,
      });

      return responseHandler(response, {
        groupName: 'useCancelDigitalDelivery',
        notFoundPath: '/music-product-digital',
        onNotFound: () => {},
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.cancel.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCancelAllDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useCancelAllDigitalDelivery'
  );
  return useMutation<{}, DefaultError, ICancelAllDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.cancel.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDelivery/CancelAll',
        body,
      });

      return responseHandler(response, {
        groupName: 'useCancelAllDigitalDelivery',
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
        onNotFound: () => {},
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.cancel.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useAcceptAllDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useAcceptAllDigitalDelivery'
  );
  return useMutation<{}, DefaultError, IAcceptAllDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDelivery/AcceptAll',
        body,
      });

      return responseHandler(response, {
        groupName: 'useAcceptAllDigitalDelivery',
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useAcceptDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useAcceptDigitalDelivery'
  );
  return useMutation<{}, DefaultError, IAcceptDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'DigitalDelivery/Accept',
        body,
      });

      return responseHandler(response, {
        groupName: 'useAcceptDigitalDelivery',
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useWithdrawnAllDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useWithdrawnAllDigitalDelivery'
  );
  return useMutation<{}, DefaultError, IWithdrawAllDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.withdrawn.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'DigitalDelivery/WithdrawnAll',
        body,
      });

      return responseHandler(response, {
        groupName: 'useWithdrawnAllDigitalDelivery',
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.withdrawn.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useWithdrawnDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useWithdrawnDigitalDelivery'
  );
  return useMutation<{}, DefaultError, IWithdrawDigitalDelivery>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.withdrawn.loading', {
          what: i18next.t('digital-delivery.what'),
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'DigitalDelivery/Withdrawn',
        body,
      });

      return responseHandler(response, {
        groupName: 'useWithdrawnDigitalDelivery',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.withdrawn.success', {
          what: i18next.t('digital-delivery.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetUnsupportedRoles = (
  digitalMusicProductId: string,
  salesChannelId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUnsupportedRolesResponse, DefaultError>({
    queryKey: ['unsupported-roles', digitalMusicProductId, salesChannelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'MusicProductDigital/Deliveries/UnsuportedRoles',
        params: { digitalMusicProductId, salesChannelId },
      });

      return responseHandler(response, {
        groupName: `useGetUnsupportedRoles - ${digitalMusicProductId} - ${salesChannelId}`,
        notFoundPath: '/music-product-digital',
      });
    },
    enabled,
    retry: false,
  });
};

export const useGetCheckExternalIds = (productId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<Array<{ id: string; nickname: string }>, DefaultError>({
    queryKey: ['check-external-ids', productId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'MusicProductDigital/CheckExternalIds',
        params: { productId },
      });

      return responseHandler(response, {
        groupName: `useGetCheckExternalIds - ${productId}`,
        notFoundPath: '/music-product-digital',
      });
    },
    retry: false,
  });
};

export const useGetDigitalDeliveryHistory = (
  productId: string,
  channelId: string,
  providerId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDigitalDeliveryHistoryItem[], DefaultError>({
    queryKey: ['digital-delivery-history', productId, channelId, providerId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'DigitalDelivery/HistoryByProductChannelProvider',
        params: { productId, channelId, providerId },
      });

      return responseHandler(response, {
        groupName: `useGetDigitalDeliveryHistory - ${productId}`,
        notFoundPath: '/music-product-digital',
      });
    },
    enabled,
    retry: false,
  });
};
